import * as React from 'react';
import { FC, memo, useCallback } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { useIntl } from 'react-intl';

import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import Animation from 'components/lottie-animations/favorite-loading/favorite-loading';
import Picture from 'components/picture';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import OfferLocked from 'pages/loyalty/offer-locked/offer-locked';
import { IncentiveSource } from 'state/loyalty/types';
import noop from 'utils/noop';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';
import { ParsedRewardData } from '../incentive-details/types';

import {
  BackgroundImage,
  CardContainer,
  DefaultBackground,
  ImageWrapper,
  IncentiveCardDescription,
  InfoContainer,
  LoadingWrapper,
  RewardText,
  StyledBadge,
  StyledClickableContainer,
  StyledRewardLock,
  WrapperButton,
} from './incentive-card.styled';

export interface IIncentiveCard {
  incentiveData: ParsedRewardData;
  handleClick: ILoyaltyRewardsViewProps['handleClick'];
  children?: React.ReactNode;
  locked?: boolean;
  favorited?: boolean;
  favoriteEnabled?: boolean;
  favoriteLoading?: boolean;
  handleToggleFavorite?: (sanityId: string, name: string) => void;
  isQuestChallengeIcentive?: boolean;
}

export const IncentiveCard: FC<IIncentiveCard> = memo(
  ({
    incentiveData,
    handleClick,
    children,
    locked = false,
    favorited = false,
    favoriteEnabled = false,
    favoriteLoading = false,
    handleToggleFavorite = noop,
    isQuestChallengeIcentive = false,
  }) => {
    const { formatMessage } = useIntl();
    const { image, imageDescription, name, id, loyaltyEngineId, backgroundImage, metadata } =
      incentiveData;

    const { featureInvitationCode } = useFeatureInvitationCode();
    const questBenefit =
      featureInvitationCode?.invitationCodeBenefit &&
      featureInvitationCode?.invitationCodeBenefit[0];

    const incentiveDetails = {
      name: isQuestChallengeIcentive ? questBenefit?.name?.locale : name,
      description: isQuestChallengeIcentive ? questBenefit?.howToRedeem?.localeRaw : '',
      image: isQuestChallengeIcentive ? questBenefit?.image?.locale : image,
      backgroundImage: <DefaultBackground />,
    };

    // Offers will use the EngineId as the URL identifier because the SanityId might be the same for multiple PersonalizedOffers.
    // Rewards will use the SanityId until we are sure we don't have any deep links to specific Rewards.
    const incentiveSanityId = id;
    const incentiveName = name;

    const showPromoCodeLabel = metadata?.some(
      data => data?.value === IncentiveSource.PROMOTION_CODE
    );
    const promoCodeLabel = showPromoCodeLabel
      ? formatMessage({ id: 'promoCodeBadgeLabel' })
      : undefined;

    const handleFavoriteClick = useCallback(
      event => {
        event.preventDefault();
        event.stopPropagation();
        handleToggleFavorite(id, name);
      },
      [handleToggleFavorite, id, name]
    );

    return (
      <CardContainer>
        <StyledClickableContainer
          onClick={handleClick({
            incentiveName,
            loyaltyEngineId,
            incentiveSanityId,
            isQuestChallengeIcentive,
          })}
          isLocked={locked}
          isQuestChallengeIcentive={isQuestChallengeIcentive}
        >
          {incentiveDetails.image && (
            <ImageWrapper>
              {locked && <StyledRewardLock />}
              {isQuestChallengeIcentive && <OfferLocked />}
              {isQuestChallengeIcentive
                ? incentiveDetails.backgroundImage
                : backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
              <Picture
                image={incentiveDetails.image}
                alt={imageDescription}
                {...incentiveCardImageDefaultProps}
              />
            </ImageWrapper>
          )}

          <InfoContainer>
            <RewardText>{incentiveDetails.name}</RewardText>
            {isQuestChallengeIcentive ? (
              <IncentiveCardDescription content={incentiveDetails.description} />
            ) : (
              children
            )}

            {promoCodeLabel && (
              <StyledBadge>
                <Icon icon="offer" color="black" width="18px" height="18px" />
                {promoCodeLabel}
              </StyledBadge>
            )}
          </InfoContainer>
          {favoriteEnabled && (
            <WrapperButton>
              <ButtonForIconFav
                data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
                onClick={handleFavoriteClick}
                aria-label={formatMessage({
                  id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                })}
                isTransparent={true}
              >
                {favoriteLoading ? (
                  <LoadingWrapper>
                    <Animation />
                  </LoadingWrapper>
                ) : (
                  <FavoriteIcon isFavorite={favorited} />
                )}
              </ButtonForIconFav>
            </WrapperButton>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
